import GetAppHeaders from '@omni/kit/Downloader/GetAppHeaders';
import { isUrlAuthorizedForSubsplashDomains } from '@omni/kit/auth';
import countries from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/countries.json';
import { Country } from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/types';
import TokensService from '@omni/kit/services/TokensService';
import { useEffect, useMemo, useState } from 'react';

export const useSupportedCountries = (
  supportedCountriesUrl?: string
): {
  supportedCountries: Country[];
  firstSupportedCountry: Country | undefined;
  isLoadingCountries: boolean;
} => {
  const [supportedCountriesData, setSupportedCountriesData] =
    useState<SupportedCountriesResponse | null>(null);
  const [isLoadingCountries, setIsLoadingCountries] = useState(
    Boolean(supportedCountriesUrl)
  );

  useEffect(() => {
    const fetchCountriesData = async () => {
      if (!supportedCountriesUrl) {
        setIsLoadingCountries(false);

        return;
      }

      try {
        const data = await fetchSupportedCountries(supportedCountriesUrl);

        if (data) {
          setSupportedCountriesData(data);
        }
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountriesData();
  }, [supportedCountriesUrl]);

  // Memoize since this is a static list and only needs to be computed once.
  const supportedCountries = useMemo(() => {
    // If no data fetched or still loading, return all countries as fallback
    if (!supportedCountriesData || isLoadingCountries) {
      return countries;
    }

    const parsedSupportedCountries = {
      ...supportedCountriesData.login_with_phone_supported_countries.default,
      ...supportedCountriesData.login_with_phone_supported_countries
        .other_supported_countries,
    } as Record<Country['regionCode'], boolean>;

    // If other_supported_countries is blank/empty, return all countries
    const otherSupportedCountries =
      supportedCountriesData.login_with_phone_supported_countries
        .other_supported_countries;
    const isOtherSupportedCountriesEmpty =
      Object.keys(otherSupportedCountries).length === 0;

    if (isOtherSupportedCountriesEmpty) {
      return countries;
    }

    const filtered = countries.filter(
      (country) => parsedSupportedCountries[country.regionCode]
    );

    // Return only the supported countries. Otherwise, return all countries.
    if (filtered.length === 0) {
      return countries;
    }

    // Get the first supported country code to put it at the top
    const firstSupportedCountryCode = Object.keys(parsedSupportedCountries)[0];

    // Sort filtered countries to put the first supported country at the top
    const sortedFiltered = [...filtered].sort((a, b) => {
      if (a.regionCode === firstSupportedCountryCode) return -1;

      if (b.regionCode === firstSupportedCountryCode) return 1;

      return 0;
    });

    return sortedFiltered;
  }, [supportedCountriesData, isLoadingCountries]);

  // Get the first supported country for default selection
  const firstSupportedCountry = useMemo(() => {
    if (!supportedCountriesData) {
      return countries.find((country) => country.regionCode === 'US');
    }

    const allSupportedCountries = {
      ...supportedCountriesData.login_with_phone_supported_countries.default,
      ...supportedCountriesData.login_with_phone_supported_countries
        .other_supported_countries,
    };

    const firstSupportedCountryCode = Object.keys(allSupportedCountries)[0];
    const firstSupportedCountry = countries.find(
      (country) => country.regionCode === firstSupportedCountryCode
    );

    return (
      firstSupportedCountry ||
      countries.find((country) => country.regionCode === 'US')
    );
  }, [supportedCountriesData]);

  return {
    supportedCountries,
    firstSupportedCountry,
    isLoadingCountries,
  };
};

export interface SupportedCountriesResponse {
  login_with_phone_supported_countries: {
    default: Record<string, boolean>;
    other_supported_countries: Record<string, boolean>;
  };
}

const fetchSupportedCountries = async (
  supportedCountriesUrl: string
): Promise<SupportedCountriesResponse | null> => {
  try {
    const token = await TokensService.getGuestToken();

    const isSubsplashRequest = isUrlAuthorizedForSubsplashDomains(
      supportedCountriesUrl
    );

    if (isSubsplashRequest && !token) {
      return null;
    }

    let appHeaders: Record<string, string> = {};
    try {
      appHeaders = await GetAppHeaders();
    } catch {}

    const response = await fetch(supportedCountriesUrl, {
      headers: {
        ...appHeaders,
        ...(isSubsplashRequest && {
          Authorization: `Bearer ${token}`,
        }),
      },
    });

    if (response.ok) {
      const data: SupportedCountriesResponse = await response.json();

      return data;
    }

    return null;
  } catch {
    return null;
  }
};
